import {
  LEFT_VIEW_DOCUMENT_ID,
  RIGHT_VIEW_DOCUMENT_ID,
  SIDE_BY_SIDE_VIEW_LEGACY_COMBINATION_STATEMENT,
  SIDE_BY_SIDE_VIEW_OCR_COMBINATION_STATEMENT,
  TIEOUT_SYNCSCROLL_SOURCEID,
  TIEOUT_SYNCSCROLL_TARGETID,
} from 'constants/feature/statement-content-constants';

const STATEMENT_PAGE_PADDING = 16;
const SYNC_SCROLL_TIMEOUT = 2000;

export const onSyncScroll = (
  isUserScrollingLeftView,
  syncScrollIds,
  blacklineViewMode,
) => {
  const sourceId = syncScrollIds?.sourceId
    ? syncScrollIds.sourceId
    : sessionStorage.getItem(TIEOUT_SYNCSCROLL_SOURCEID);
  const targetId = syncScrollIds?.targetId
    ? syncScrollIds.targetId
    : sessionStorage.getItem(TIEOUT_SYNCSCROLL_TARGETID);
  let leftView = !blacklineViewMode
    ? document.getElementById(sourceId)
    : document.getElementById(LEFT_VIEW_DOCUMENT_ID);
  let rightView = !blacklineViewMode
    ? document.getElementById(targetId)
    : document.getElementById(RIGHT_VIEW_DOCUMENT_ID);

  if (leftView && rightView) {
    if (isUserScrollingLeftView) {
      rightView.scrollTop = leftView.scrollTop;
    } else {
      leftView.scrollTop = rightView.scrollTop;
    }
  }
};

const scrollListeners = new WeakMap();

const getOrCreateScrollHandler = (
  view,
  isLeft,
  syncScrollIds,
  blacklineViewMode,
) => {
  let handler = scrollListeners.get(view);
  if (!handler) {
    handler = () => onSyncScroll(isLeft, syncScrollIds, blacklineViewMode);
    scrollListeners.set(view, handler);
  }
  return handler;
};

export const registerLeftScrollListeners = (
  leftView,
  syncScrollIds,
  blacklineViewMode,
) => {
  if (leftView) {
    deRegisterLeftScrollListeners(leftView);
    const handler = getOrCreateScrollHandler(
      leftView,
      true,
      syncScrollIds,
      blacklineViewMode,
    );
    leftView.addEventListener('scroll', handler);
  }
};

export const registerRightScrollListeners = (
  rightView,
  syncScrollIds,
  blacklineViewMode,
) => {
  if (rightView) {
    deRegisterRightScrollListeners(rightView);
    const handler = getOrCreateScrollHandler(
      rightView,
      false,
      syncScrollIds,
      blacklineViewMode,
    );
    rightView.addEventListener('scroll', handler);
  }
};

export const deRegisterLeftScrollListeners = (leftView) => {
  if (leftView) {
    const handler = scrollListeners.get(leftView);
    if (handler) {
      leftView.removeEventListener('scroll', handler);
      scrollListeners.delete(leftView);
    }
  }
};

export const deRegisterRightScrollListeners = (rightView) => {
  deRegisterLeftScrollListeners(rightView);
};

const getElementPositionOnPage = (dpi, zoom, elementDetails, sectionIdList) => {
  const containerWidth =
    document.getElementById('statement-content-panel-ocr__document-id')
      ?.offsetWidth - 45;
  const width = sectionIdList.sectionIds.length
    ? sectionIdList.sectionIds[0].width
    : 0;
  const dynamicScalingFactor = (containerWidth * dpi) / (150 * width);
  const scale = dynamicScalingFactor * (150 / dpi);
  const scaleWithZoom = scale * zoom.zoomRatio;
  let x = 0,
    y = 0;
  [x, y] = JSON.parse(elementDetails.polygonInPixel);
  const elementY = y * scaleWithZoom;
  const elementX = x * scaleWithZoom;
  return { elementX, elementY };
};

export const getTotalSectionHeight = (
  sectionId,
  isLegacyStatementsSelected = false,
  documentId,
) => {
  const canvas =
    isLegacyStatementsSelected && documentId
      ? document.getElementById(documentId)
      : document.getElementById('canvas-1');
  const { height } = canvas.getBoundingClientRect();
  const heightOfOnePage = height + STATEMENT_PAGE_PADDING * 2;
  const totalPageHeight = heightOfOnePage * (sectionId - 1);
  return totalPageHeight;
};

export const syncScrollIntoView = ({
  isSyncScrollingMode,
  rowTitle,
  elementDetails,
  zoom,
  dpi,
  sectionIdList,
  syncScrollIds,
  blacklineViewMode,
}) => {
  const rightView = document.getElementById(RIGHT_VIEW_DOCUMENT_ID);
  const leftView = document.getElementById(LEFT_VIEW_DOCUMENT_ID);

  const totalPageHeight = getTotalSectionHeight(elementDetails.sectionId);
  const { elementX, elementY } = getElementPositionOnPage(
    dpi,
    zoom,
    elementDetails,
    sectionIdList,
  );

  const top = totalPageHeight + elementY;
  const left = elementX - STATEMENT_PAGE_PADDING * 2;
  const options = { top, left, behavior: 'smooth' };

  if (isSyncScrollingMode) {
    deRegisterLeftScrollListeners(leftView);
    deRegisterRightScrollListeners(rightView);

    if (leftView && rightView) {
      leftView.scrollTo(options);
      rightView.scrollTo(options);
    }

    setTimeout(() => {
      registerLeftScrollListeners(leftView, syncScrollIds, blacklineViewMode);
      registerRightScrollListeners(rightView, syncScrollIds, blacklineViewMode);
    }, SYNC_SCROLL_TIMEOUT);
  } else {
    const isLeftSectionScroll = rowTitle === 'Deleted';
    const view = isLeftSectionScroll ? leftView : rightView;
    view.scrollTo(options);
  }
};

export const isBothLegacyOrOCRInSideBySideView = (
  selectedStatement,
  leftSelectedStatement,
) => {
  const isSourceLegacy = !selectedStatement.isOCR;
  const isTargetLegacy = !leftSelectedStatement.isOCR;

  if (isSourceLegacy === isTargetLegacy) {
    return isSourceLegacy
      ? SIDE_BY_SIDE_VIEW_LEGACY_COMBINATION_STATEMENT // it returns true if both source and target statement are Legacy statements
      : SIDE_BY_SIDE_VIEW_OCR_COMBINATION_STATEMENT; // it returns true if both source and target statement are OCR statements
  }
};

export const syncSideBySideScrollIntoView = async ({
  sectionId,
  isLeftView,
  syncScrollIds,
  sideBySideSyncScroll,
  selectedStatement,
  leftSelectedStatement,
}) => {
  const leftView = document.getElementById(syncScrollIds.sourceId);
  const rightView = document.getElementById(syncScrollIds.targetId);
  const isLegacyStatementsSelected =
    isBothLegacyOrOCRInSideBySideView(
      selectedStatement,
      leftSelectedStatement,
    ) === SIDE_BY_SIDE_VIEW_LEGACY_COMBINATION_STATEMENT;

  const documentId = isLeftView
    ? syncScrollIds.sourceId
    : syncScrollIds.targetId;
  const totalPageHeight = getTotalSectionHeight(
    sectionId,
    isLegacyStatementsSelected,
    documentId,
  );

  const top = totalPageHeight;
  const options = { top, behavior: 'smooth' };

  if (sideBySideSyncScroll) {
    deRegisterLeftScrollListeners(leftView);
    deRegisterRightScrollListeners(rightView);

    if (leftView && rightView) {
      leftView.scrollTo(options);
      rightView.scrollTo(options);
    }

    setTimeout(() => {
      registerLeftScrollListeners(leftView);
      registerRightScrollListeners(rightView);
    }, SYNC_SCROLL_TIMEOUT);
  }
};
